import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../../../contexts/AuthContext';
import { useToast } from '../../../../contexts/ToastContext';
import { ROLES } from '../../../../constants/roles';
import { hasSuperAdminView, roleMatches } from '../../../../utils/rolePermissions';
import { filterRosterEligibleCrew } from '../../../../utils/crewRosterEligibility';
import dayjs from 'dayjs';

// Use same-origin API by default in browsers; allow override via REACT_APP_API_URL for local/dev tools
const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Custom hook for managing Crew Duty Roster data fetching
 */
export const useCrewDutyRosterData = (filters, tabValue, calendarStartDate, calendarEndDate) => {
  const { user } = useAuth();
  const { showToast } = useToast();

  const isOpsRoster = roleMatches(user?.role, [
    ROLES.OPERATIONS_MANAGER,
    ROLES.OPERATIONS_EXECUTIVE,
    ROLES.ACCOUNTABLE_MANAGER,
    ROLES.SUPER_ADMIN
  ]);
  const isCrewSelfView = roleMatches(user?.role, [ROLES.CREW]);
  const [entries, setEntries] = useState([]);
  const [crewMembers, setCrewMembers] = useState([]);
  const [availableCrew, setAvailableCrew] = useState([]);
  const [aircraftList, setAircraftList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [calendarAvailability, setCalendarAvailability] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);

  const fetchEntries = async (signal = null) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.crewId) params.append('crewId', filters.crewId);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.dutyType) params.append('dutyType', filters.dutyType);
      if (filters.status) params.append('status', filters.status);

      const config = {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        ...(signal && { signal })
      };
      const response = await axios.get(`${API_URL}/ops/crew-duty-roster?${params}`, config);

      if (signal?.aborted) return;

      setEntries(response.data);
    } catch (error) {
      if ((axios.isCancel && axios.isCancel(error)) || error?.code === 'ERR_CANCELED') return;
      console.error('Error fetching duty roster entries:', error);
      showToast('Failed to fetch duty roster entries', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableCrew = async (signal = null) => {
    try {
      const params = new URLSearchParams();
      if (filters.crewId) params.append('crewId', filters.crewId);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      // Only show crew explicitly marked AVAILABLE in the given window
      params.append('availabilityStatus', 'AVAILABLE');

      const config = {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        ...(signal && { signal })
      };
      const response = await axios.get(`${API_URL}/ops/crew-duty-roster/availability?${params.toString()}`, config);

      if (signal?.aborted) return;

      setAvailableCrew(response.data || []);
    } catch (error) {
      if (axios.isCancel && axios.isCancel(error)) return;
      console.error('Error fetching available crew:', error);
      // Intentionally no toast: this is an enhancement panel and should not spam errors
    }
  };

  const fetchCrewMembers = async (signal = null) => {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const config = { headers, ...(signal && { signal }) };

      // SUPER_ADMIN / SPECTATOR can see all users; filter to crew on client-side
      // If /users endpoint fails (403), fall back to crew-list endpoint
      if (hasSuperAdminView(user?.role)) {
        try {
          const response = await axios.get(`${API_URL}/users`, config);
          if (signal?.aborted) return;
          const crewOnly = filterRosterEligibleCrew(response.data || []);
          setCrewMembers(crewOnly);
        } catch (usersError) {
          if (axios.isCancel && axios.isCancel(usersError)) return;
          // If /users endpoint returns 403, fall back to crew-list endpoint
          if (usersError.response?.status === 403) {
            console.warn('Access denied to /users endpoint, falling back to crew-list');
            const response = await axios.get(`${API_URL}/ops/crew-duty-roster/crew-list`, config);
            if (signal?.aborted) return;
            setCrewMembers(response.data || []);
          } else {
            throw usersError; // Re-throw if it's a different error
          }
        }
      } else {
        // Operations / Accountable Manager get a dedicated, limited crew list
        const response = await axios.get(`${API_URL}/ops/crew-duty-roster/crew-list`, config);
        if (signal?.aborted) return;
        setCrewMembers(response.data || []);
      }
    } catch (error) {
      if (axios.isCancel && axios.isCancel(error)) return;
      console.error('Error fetching crew members:', error);
      // Show user-friendly error message
      if (error.response?.status === 403) {
        showToast('You do not have permission to view crew members', 'error');
      } else {
        showToast('Failed to fetch crew members. Please try again.', 'error');
      }
    }
  };

  const fetchAircraft = async (signal = null) => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        ...(signal && { signal })
      };
      const response = await axios.get(`${API_URL}/aircraft`, config);
      if (signal?.aborted) return;
      const list = (response.data || []).filter((a) => a.isActive !== false);
      setAircraftList(list);
    } catch (error) {
      if (axios.isCancel && axios.isCancel(error)) return;
      console.error('Error fetching aircraft:', error);
    }
  };

  const fetchDashboard = async (signal = null) => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        ...(signal && { signal })
      };
      const response = await axios.get(`${API_URL}/ops/crew-duty-roster/dashboard`, config);

      if (signal?.aborted) return;

      setDashboardData(response.data);
    } catch (error) {
      if (axios.isCancel && axios.isCancel(error)) return;
      console.error('Error fetching dashboard data:', error);
    }
  };

  const fetchCalendarAvailability = async (signal = null) => {
    setCalendarLoading(true);
    try {
      const params = new URLSearchParams({
        startDate: calendarStartDate.format('YYYY-MM-DD'),
        endDate: calendarEndDate.format('YYYY-MM-DD')
      });

      const config = {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        ...(signal && { signal })
      };
      const response = await axios.get(`${API_URL}/ops/crew-duty-roster/calendar/availability?${params.toString()}`, config);

      if (signal?.aborted) return;

      setCalendarAvailability(response.data || []);
    } catch (error) {
      if ((axios.isCancel && axios.isCancel(error)) || error?.code === 'ERR_CANCELED') return;
      console.error('Error fetching calendar availability:', error);
      showToast('Failed to fetch calendar availability', 'error');
    } finally {
      setCalendarLoading(false);
    }
  };

  // Fetch entries and available crew when filters change
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    // Crew Hours / Rostering / Availability / Policies / DGCA load their own data —
    // skip roster table + availability polls that only spam 403 toasts on those tabs.
    // Crew self-view: tab 0 = roster, tab 1 = My Hours (no table poll needed on hours).
    const skipRosterTableFetches = isOpsRoster
      ? tabValue >= 3
      : isCrewSelfView && tabValue === 1;

    if (!skipRosterTableFetches) {
      fetchEntries(signal);
      if (isOpsRoster) fetchAvailableCrew(signal);
    }

    // Fetch crew list and aircraft for roles allowed to manage duty roster entries
    if (isOpsRoster) {
      fetchCrewMembers(signal);
      fetchAircraft(signal);
    }

    if (isOpsRoster && tabValue === 2) {
      fetchDashboard(signal);
    }

    return () => controller.abort();
  }, [tabValue, filters, user, isOpsRoster, isCrewSelfView]);

  // Separate useEffect for calendar data to avoid unnecessary re-renders
  useEffect(() => {
    if (!isOpsRoster || tabValue !== 1) return;
    const controller = new AbortController();
    fetchCalendarAvailability(controller.signal);
    return () => controller.abort();
  }, [tabValue, calendarStartDate.format('YYYY-MM-DD'), calendarEndDate.format('YYYY-MM-DD'), isOpsRoster]);

  return {
    entries,
    setEntries,
    crewMembers,
    availableCrew,
    aircraftList,
    loading,
    dashboardData,
    calendarAvailability,
    calendarLoading,
    fetchEntries,
    fetchAvailableCrew,
    fetchCrewMembers,
    fetchDashboard,
    fetchCalendarAvailability
  };
};
